import { create } from '@bufbuild/protobuf'
import { createConnectQueryKey, useMutation, useQuery } from '@connectrpc/connect-query'
import { useQueryClient } from '@tanstack/react-query'

import { useListUsers } from '@/api/list-users.hook'
import type { User } from '@/gen/customer/management/v1/service_pb'
import {
  addSolutionAssignee,
  getDiscoverySolution,
  listDiscoverySolutions,
  listSolutionCloudAccounts,
  listSolutionDevices,
  removeSolutionAssignee,
  setCloudAccountClassification,
  setSolutionDismissed,
  setSolutionSuggestedThirdParty,
} from '@/gen/inventory/v1/company_service-CompanyInventoryService_connectquery'
import {
  AddSolutionAssigneeRequestSchema,
  CompanyInventoryService,
  type ListDiscoverySolutionsResponse,
  RemoveSolutionAssigneeRequestSchema,
} from '@/gen/inventory/v1/company_service_pb'

import { useFeatureFlagEnabled } from '@/lib/config/featureflag'
import { defaultTransport } from '@/lib/connect/connect-transport'
import { sortByStringKey } from '@/lib/string/string-utils'

import { useToast } from '@/components/lemmala-design-system/toast/use-toast'

// --- Query Key Factories ---

export const getListDiscoverySolutionsQueryKey = () =>
  createConnectQueryKey({
    schema: CompanyInventoryService.method.listDiscoverySolutions,
    transport: defaultTransport,
    cardinality: 'finite',
  })

export const getGetDiscoverySolutionQueryKey = () =>
  createConnectQueryKey({
    schema: CompanyInventoryService.method.getDiscoverySolution,
    transport: defaultTransport,
    cardinality: 'finite',
  })

export const getListSolutionCloudAccountsQueryKey = () =>
  createConnectQueryKey({
    schema: CompanyInventoryService.method.listSolutionCloudAccounts,
    transport: defaultTransport,
    cardinality: 'finite',
  })

// --- Query Hooks ---

export const useListDiscoverySolutions = (input?: {
  includeDismissed?: boolean
  enabled?: boolean
}) => {
  const queryState = useQuery(
    listDiscoverySolutions,
    { includeDismissed: input?.includeDismissed ?? false },
    { enabled: input?.enabled ?? true },
  )

  return {
    ...queryState,
    data: queryState.data?.solutions ?? [],
  }
}

// Drawer detail for a single unlinked discovery solution: usage + permissions + suggested
// company. Mirrors useGetCompanyDrawerMetadata; users are gated on the same feature flag.
export const useGetDiscoverySolution = (solutionId?: string) => {
  const { isEnabled } = useFeatureFlagEnabled('third-party-usage-list-users')
  return useQuery(
    getDiscoverySolution,
    { solutionId, withUsers: isEnabled },
    { enabled: !!solutionId },
  )
}

// The endpoints a discovery solution is installed on, for the drawer's Devices tab. Only worth
// fetching for solutions the list already reported devices for.
export const useListSolutionDevices = (solutionId?: string, enabled = true) => {
  const queryState = useQuery(
    listSolutionDevices,
    { solutionId },
    { enabled: !!solutionId && enabled },
  )

  return {
    ...queryState,
    devices: queryState.data?.devices ?? [],
  }
}

// The cloud accounts a discovery solution was found in, for the drawer's Cloud Accounts tab.
export const useListSolutionCloudAccounts = (solutionId?: string, enabled = true) => {
  const queryState = useQuery(
    listSolutionCloudAccounts,
    { solutionId },
    { enabled: !!solutionId && enabled },
  )

  return {
    ...queryState,
    cloudAccounts: queryState.data?.cloudAccounts ?? [],
  }
}

// --- Mutation Hooks ---

// The classification is per account, so it moves every application seen in it: both the account
// list and the solutions listing (its environment column) are stale after a change.
export const useSetCloudAccountClassification = ({ toastOnError = true } = {}) => {
  const queryClient = useQueryClient()
  const { toast } = useToast()

  return useMutation(setCloudAccountClassification, {
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: getListSolutionCloudAccountsQueryKey() })
      queryClient.invalidateQueries({ queryKey: getListDiscoverySolutionsQueryKey() })
    },
    onError: (error) => {
      if (!toastOnError) return
      toast({
        title: 'Error',
        description: error.message || 'Failed to change the cloud account classification',
        variant: 'destructive',
      })
    },
  })
}

export const useSetSolutionDismissed = (solutionName?: string) => {
  const queryClient = useQueryClient()
  const { toast } = useToast()

  const name = solutionName?.trim() || 'App'

  return useMutation(setSolutionDismissed, {
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: getListDiscoverySolutionsQueryKey() })
      toast({
        variant: 'success',
        title: variables.dismissed ? `${name} dismissed` : `${name} restored`,
        description: variables.dismissed
          ? 'The app has been moved to the dismissed list.'
          : 'The app has been moved back to the detected list.',
      })
    },
    onError: (error, variables) => {
      toast({
        title: 'Error',
        description:
          error.message ||
          (variables.dismissed ? 'Failed to dismiss app' : 'Failed to restore app'),
        variant: 'destructive',
      })
    },
  })
}

const assigneeDisplayName = (user: Pick<User, 'name' | 'email'>) => user.name || user.email

export const addAssigneeToListData = (
  data: ListDiscoverySolutionsResponse,
  solutionId: string,
  newAssignee: User,
): ListDiscoverySolutionsResponse => ({
  ...data,
  solutions: data.solutions.map((solution) => {
    if (solution.id !== solutionId) return solution
    if (solution.assignees.some((assignee) => assignee.id === newAssignee.id)) return solution
    return {
      ...solution,
      assignees: [...solution.assignees, newAssignee].sort(sortByStringKey(assigneeDisplayName)),
    }
  }),
})

export const removeAssigneeFromListData = (
  data: ListDiscoverySolutionsResponse,
  solutionId: string,
  userId: string,
): ListDiscoverySolutionsResponse => ({
  ...data,
  solutions: data.solutions.map((solution) =>
    solution.id !== solutionId
      ? solution
      : {
          ...solution,
          assignees: solution.assignees.filter((assignee) => assignee.id !== userId),
        },
  ),
})

export const useAddSolutionAssignee = () => {
  const queryClient = useQueryClient()
  const { toast } = useToast()
  const { data: users } = useListUsers()
  const listQueryKey = getListDiscoverySolutionsQueryKey()

  // Patch ListDiscoverySolutions in place — a full refetch is too slow for the assignee click.
  return useMutation(addSolutionAssignee, {
    onMutate: async (variables) => {
      const { solutionId, userId } = create(AddSolutionAssigneeRequestSchema, variables)

      await queryClient.cancelQueries({ queryKey: listQueryKey })

      const previousQueries = queryClient.getQueriesData<ListDiscoverySolutionsResponse>({
        queryKey: listQueryKey,
      })

      const newAssignee = users.find((user) => user.id === userId)
      if (newAssignee) {
        for (const [key, data] of previousQueries) {
          if (!data) continue
          queryClient.setQueryData<ListDiscoverySolutionsResponse>(
            key,
            addAssigneeToListData(data, solutionId, newAssignee),
          )
        }
      }

      return { previousQueries }
    },
    onError: (error, _variables, context) => {
      context?.previousQueries.forEach(([key, data]) => {
        queryClient.setQueryData<ListDiscoverySolutionsResponse>(key, data)
      })
      toast({
        title: 'Error',
        description: error.message || 'Failed to assign user',
        variant: 'destructive',
      })
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: listQueryKey })
    },
  })
}

export const useRemoveSolutionAssignee = () => {
  const queryClient = useQueryClient()
  const { toast } = useToast()
  const listQueryKey = getListDiscoverySolutionsQueryKey()

  return useMutation(removeSolutionAssignee, {
    onMutate: async (variables) => {
      const { solutionId, userId } = create(RemoveSolutionAssigneeRequestSchema, variables)

      await queryClient.cancelQueries({ queryKey: listQueryKey })

      const previousQueries = queryClient.getQueriesData<ListDiscoverySolutionsResponse>({
        queryKey: listQueryKey,
      })

      for (const [key, data] of previousQueries) {
        if (!data) continue
        queryClient.setQueryData<ListDiscoverySolutionsResponse>(
          key,
          removeAssigneeFromListData(data, solutionId, userId),
        )
      }

      return { previousQueries }
    },
    onError: (error, _variables, context) => {
      context?.previousQueries.forEach(([key, data]) => {
        queryClient.setQueryData<ListDiscoverySolutionsResponse>(key, data)
      })
      toast({
        title: 'Error',
        description: error.message || 'Failed to unassign user',
        variant: 'destructive',
      })
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: listQueryKey })
    },
  })
}

export const useSetSolutionSuggestedThirdParty = () => {
  const queryClient = useQueryClient()
  const { toast } = useToast()

  return useMutation(setSolutionSuggestedThirdParty, {
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: getListDiscoverySolutionsQueryKey() })
      queryClient.invalidateQueries({ queryKey: getGetDiscoverySolutionQueryKey() })
    },
    onError: (error) => {
      toast({
        title: 'Error',
        description: error.message || 'Failed to update the suggested third-party',
        variant: 'destructive',
      })
    },
  })
}
